import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { usePageMeta } from '../hooks/usePageMeta.js'
import flowIcon from '@assets/image_1777447102475.png'

const TMDB_IMG = 'https://image.tmdb.org/t/p/w500'
const TMDB_LOGO = 'https://image.tmdb.org/t/p/w45'
const BASE = 'https://onceupona.stream'

function parseSlug(slug) {
  const m = slug.match(/-(\d{4})$/)
  if (!m) return { title: slug.replace(/-/g, ' '), year: null }
  const year = m[1]
  const raw = slug.slice(0, slug.lastIndexOf(`-${year}`)).replace(/-/g, ' ')
  const title = raw.replace(/\b\w/g, c => c.toUpperCase())
  return { title, year }
}

export default function WatchPage() {
  const { slug } = useParams()
  const [movie, setMovie]   = useState(null)
  const [loading, setLoading] = useState(true)

  const { title: slugTitle, year } = parseSlug(slug)

  useEffect(() => {
    fetch(`/api/where-to-watch/${encodeURIComponent(slug)}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { setMovie(data); setLoading(false) })
      .catch(() => setLoading(false))
  }, [slug])

  const title = movie
    ? `Where to Stream ${movie.title} (${year}) | Flow`
    : `Where to Stream ${slugTitle} | Flow`

  const description = movie
    ? `Find out where to stream ${movie.title}${year ? ` (${year})` : ''} right now. Flow tracks Netflix, Prime, Disney+, and more platforms.${movie.providers?.length ? ` Currently available on ${[...new Set(movie.providers.map(p => p.provider_name))].join(', ')}.` : ''}`
    : `Find out where to stream ${slugTitle} right now on Flow — your streaming concierge.`

  const jsonLd = movie ? {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.overview || description,
    ...(movie.poster_path && { image: `${TMDB_IMG}${movie.poster_path}` }),
    ...(movie.release_date && { datePublished: movie.release_date }),
    ...(movie.genres?.length && { genre: movie.genres }),
    potentialAction: {
      '@type': 'WatchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${BASE}/?q=${encodeURIComponent(movie.title)}`,
        actionPlatform: [
          'http://schema.org/DesktopWebPlatform',
          'http://schema.org/MobileWebPlatform',
        ],
      },
    },
  } : null

  usePageMeta({ title, description, canonical: `${BASE}/where-to-watch/${slug}`, jsonLd })

  const uniqueProviders = movie?.providers
    ? movie.providers.filter((p, i, a) => a.findIndex(x => x.provider_id === p.provider_id) === i)
    : []

  return (
    <div style={{ minHeight: '100vh', background: '#030a04', color: '#c8e6cb', fontFamily: "'Inter', -apple-system, sans-serif" }}>

      {/* ── Top bar ── */}
      <div style={{
        padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 10,
        borderBottom: '1px solid rgba(34,197,94,0.08)',
        background: 'rgba(3,10,4,0.95)', backdropFilter: 'blur(12px)',
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
          <img src={flowIcon} alt="Flow" style={{ width: 30, height: 30, borderRadius: 7 }} />
          <span style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontStyle: 'italic', fontSize: 19, color: '#c8e6cb' }}>Flow</span>
        </Link>
        <span style={{ flex: 1 }} />
        <Link to="/" style={{
          fontSize: 12, fontWeight: 600, color: 'rgba(134,239,172,0.7)', textDecoration: 'none',
          background: 'rgba(22,163,74,0.08)', padding: '6px 12px',
          borderRadius: 20, border: '1px solid rgba(34,197,94,0.18)',
          WebkitTapHighlightColor: 'transparent',
        }}>← Back to Flow</Link>
      </div>

      <div style={{ maxWidth: 640, margin: '0 auto', padding: '28px 20px 60px' }}>

        {loading ? (
          <div style={{ textAlign: 'center', paddingTop: 60, color: 'rgba(134,239,172,0.5)', fontSize: 14 }}>
            Looking up streaming info…
          </div>
        ) : movie ? (
          <>
            {/* ── Movie header ── */}
            <div style={{ display: 'flex', gap: 18, marginBottom: 32, alignItems: 'flex-start' }}>
              {movie.poster_path && (
                <img
                  src={`${TMDB_IMG}${movie.poster_path}`}
                  alt={movie.title}
                  width={96}
                  style={{ borderRadius: 10, flexShrink: 0, boxShadow: '0 8px 28px rgba(0,0,0,0.55)' }}
                />
              )}
              <div>
                <h1 style={{
                  fontFamily: "'DM Serif Display', Georgia, serif", fontStyle: 'italic',
                  fontSize: 'clamp(20px,4.5vw,30px)', fontWeight: 400,
                  color: '#dce8f8', margin: '0 0 4px', lineHeight: 1.2,
                }}>{movie.title}</h1>
                {year && <div style={{ fontSize: 13, color: 'rgba(200,228,242,0.4)', marginBottom: 10 }}>{year}</div>}
                {movie.genres?.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                    {movie.genres.map(g => (
                      <span key={g} style={{
                        fontSize: 11, background: 'rgba(34,197,94,0.09)', border: '1px solid rgba(34,197,94,0.18)',
                        borderRadius: 20, padding: '3px 8px', color: 'rgba(134,239,172,0.7)',
                      }}>{g}</span>
                    ))}
                  </div>
                )}
                {movie.overview && (
                  <p style={{ fontSize: 13, color: 'rgba(200,228,242,0.5)', lineHeight: 1.6, margin: 0 }}>
                    {movie.overview.length > 220 ? movie.overview.slice(0, 220) + '…' : movie.overview}
                  </p>
                )}
              </div>
            </div>

            {/* ── Streaming availability ── */}
            <div style={{ marginBottom: 28 }}>
              <h2 style={{
                fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em',
                color: 'rgba(134,239,172,0.55)', marginBottom: 14,
              }}>Where to stream</h2>
              {uniqueProviders.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {uniqueProviders.map(p => (
                    <div key={p.provider_id} style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      background: 'rgba(4,14,7,0.8)', border: '1.5px solid rgba(34,197,94,0.12)',
                      borderRadius: 10, padding: '8px 12px',
                    }}>
                      {p.logo_path && (
                        <img src={`${TMDB_LOGO}${p.logo_path}`} alt={p.provider_name}
                          width={24} height={24} style={{ borderRadius: 5 }} />
                      )}
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'rgba(200,228,242,0.8)' }}>
                        {p.provider_name}
                      </span>
                      {p.watch_monetization_type === 'flatrate' && (
                        <span style={{
                          fontSize: 9, fontWeight: 700, letterSpacing: '0.05em',
                          color: 'rgba(134,239,172,0.65)', background: 'rgba(22,163,74,0.12)',
                          borderRadius: 4, padding: '2px 5px', textTransform: 'uppercase',
                        }}>Included</span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ fontSize: 13, color: 'rgba(200,228,242,0.35)', margin: 0 }}>
                  Not currently found on major platforms — search on Flow to check all services.
                </p>
              )}
            </div>

            {/* ── CTA ── */}
            <Link
              to={`/?q=${encodeURIComponent(movie.title)}`}
              style={{
                display: 'block', textAlign: 'center', textDecoration: 'none',
                padding: '15px 0', borderRadius: 14,
                background: 'linear-gradient(135deg, #16a34a, #15803d)',
                color: '#fff', fontSize: 15, fontWeight: 700,
                boxShadow: '0 4px 20px rgba(22,163,74,0.35)',
              }}
            >Search &ldquo;{movie.title}&rdquo; on Flow →</Link>
          </>
        ) : (
          <>
            <h1 style={{
              fontFamily: "'DM Serif Display', Georgia, serif", fontStyle: 'italic',
              fontSize: 'clamp(20px,4.5vw,28px)', fontWeight: 400, color: '#dce8f8', marginBottom: 10,
            }}>Where to stream {slugTitle}</h1>
            <p style={{ fontSize: 14, color: 'rgba(200,228,242,0.45)', marginBottom: 28, lineHeight: 1.6 }}>
              We couldn't find that title in our database yet. Try searching for it directly on Flow — we track 20+ streaming platforms in real time.
            </p>
            <Link
              to={`/?q=${encodeURIComponent(slugTitle)}`}
              style={{
                display: 'block', textAlign: 'center', textDecoration: 'none',
                padding: '14px 0', borderRadius: 14,
                background: 'linear-gradient(135deg, #16a34a, #15803d)',
                color: '#fff', fontSize: 15, fontWeight: 700,
              }}
            >Search on Flow →</Link>
          </>
        )}

        {/* ── SEO copy ── */}
        <div style={{
          marginTop: 48, paddingTop: 20, borderTop: '1px solid rgba(34,197,94,0.07)',
          fontSize: 12, color: 'rgba(200,228,242,0.25)', lineHeight: 1.8,
        }}>
          Flow is a streaming concierge that tracks availability across Netflix, Amazon Prime Video,
          Disney+, Max, Apple TV+, Hulu, Paramount+, Peacock, and more. Find anything to watch instantly.
        </div>
      </div>
    </div>
  )
}
